import { css } from '@emotion/react';
import { aigleColor } from './colorStyleGuide';

/**
 * 버튼 스타일 가이드
 * 
 * 애플리케이션 전체에서 사용되는 버튼 스타일을 관리합니다.
 * Contained, Outlined, Transparent 타입의 버튼을 제공합니다.
 * 
 * @example
 * import { aigleButtonStyles } from '@styles/buttonStyleGuide';
 * 
 * const MyButton = styled.button`
 *   ${aigleButtonStyles.contained.primary}
 * `;
 */

/**
 * 버튼 기본 스타일
 */
const baseButtonStyle = css`
  /* 브라우저 기본 스타일 완전히 제거 */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  margin: 0;
  box-shadow: none;
  
  /* 기본 레이아웃 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  
  /* 텍스트 스타일*/
  font-size: 16px;
  font-size: 1rem;
  line-height: 140%;
  text-decoration: none;
  white-space: nowrap;
  
  /* 인터랙션 */
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease-in-out;
  
  /* 초기 테두리와 아웃라인 제거 */
  border: none;
  outline: none;
  
  /* 포커스 상태 */
  &:focus-visible {
    outline: 2px solid ${aigleColor.special.focusRing};
    outline-offset: 2px;
  }
  
  /* 비활성화 상태 */
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
`;

/**
 * Contained Buttons (배경색이 있는 버튼)
 */
export const containedButton = {
  /** Primary Contained Button - 진한 파란색 */
  primary: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.brand.primary};
    color: ${aigleColor.grey.white};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.brand.primaryDark};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.brand.primaryDark};
      transform: translateY(1px);
    }
  `,

  /** Secondary Contained Button - 연한 파란색 배경에 진한 파란색 텍스트 */
  secondary: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.brand.secondary};
    color: ${aigleColor.grey.black};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.brand.secondaryDark};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.brand.secondaryDark};
      transform: translateY(1px);
    }
  `,

  /** Default Contained Button - 회색 */
  default: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.light};
    color: ${aigleColor.grey.black};
    border: none;
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.grey.dark};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.grey.dark};
      transform: translateY(1px);
    }
  `,

  /** Disabled Contained Button - 아주 연한 회색 */
  disabled: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.lighter};
    color: ${aigleColor.grey.dark};
    border: none;
  `,
};

/**
 * Outlined Buttons (테두리만 있는 버튼)
 */
export const outlinedButton = {
  /** Primary Outlined Button - 파란색 테두리와 텍스트 */
  primary: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.white};
    color: ${aigleColor.grey.black};
    border: 1px solid ${aigleColor.brand.primary};
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.alpha.primary.alpha10};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.alpha.primary.alpha20};
      transform: translateY(1px);
    }
  `,

  /** Secondary Outlined Button - 연한 파란색 테두리와 텍스트 */
  secondary: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.white};
    color: ${aigleColor.grey.black};
    border: 1px solid ${aigleColor.brand.secondary};
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.alpha.secondary.alpha10};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.alpha.secondary.alpha20};
      transform: translateY(1px);
    }
  `,

  /** Default Outlined Button - 회색 테두리와 텍스트 */
  default: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.white};
    color: ${aigleColor.grey.black};
    border: 1px solid ${aigleColor.grey.dark};
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.grey.lightest};
    }
    
    &:active:not(:disabled) {
      background-color: ${aigleColor.grey.lighter};
      transform: translateY(1px);
    }
  `,

  /** Disabled Outlined Button - 아주 연한 회색 */
  disabled: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.grey.white};
    color: ${aigleColor.grey.light};
    border: 1px solid ${aigleColor.grey.lighter};
  `,
};

/**
 * Transparent Button (배경/테두리 없는 버튼)
 */
export const transparentButton = css`
  ${baseButtonStyle}
  background-color: none;
  color: ${aigleColor.grey.darkest};
  border: none;
  
  &:hover:not(:disabled) {
    background-color: ${aigleColor.grey.lightest};
  }
  
  &:active:not(:disabled) {
    background-color: ${aigleColor.grey.lightest};
    transform: translateY(1px);
  }
  
  &:disabled {
    opacity: 0.5;
  }
`;

/**
 * 버튼 사이즈 variants
 * MUI Typography 스타일 기준으로 매핑
 */
export const buttonSize = {
  /** Small - MUI subtitle2 (12px, 140%) */
  small: css`
    padding: 4px 12px;
    font-size: 12px;
    font-size: 0.75rem;
    line-height: 140%;
    gap: 4px;
    height: 32px;
  `,

  /** Medium - MUI body1 (16px, 140%) */
  medium: css`
    font-size: 16px;
    font-size: 1rem;
    line-height: 140%;
    gap: 8px;
    height: 48px;
  `,

  /** Large - MUI body1 (16px, 140%) */
  large: css`
    font-size: 16px;
    font-size: 1rem;
    line-height: 140%;
    gap: 8px;
    height: 64px;
  `,
};

/**
 * 아이콘 버튼 스타일 (아이콘 + 텍스트)
 */
export const iconButtonStyle = css`
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

/**
 * 아이콘만 있는 버튼 스타일
 */
export const iconOnlyButton = {
  primary: css`
    ${baseButtonStyle}
    background-color: ${aigleColor.primary};
    color: ${aigleColor.text.white};
    padding: 4px;
    width: 40px;
    height: 40px!important;
    border-radius: 50%;
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.brand.primaryDark};
    }
    
    svg {
      width: 20px;
      height: 20px;
    }
  `,

  default: css`
    ${baseButtonStyle}
    background-color: transparent;
    color: ${aigleColor.grey.black};
    padding: 4px;
    width: 40px;
    height: 40px!important;
    border-radius: 50%;
    
    &:hover:not(:disabled) {
      background-color: ${aigleColor.grey.lightest};
    }
    
    svg {
      width: 20px;
      height: 20px;
    }
  `,
};

/**
 * 전체 너비 버튼
 */
export const fullWidthButton = css`
  width: 100%;
`;

/**
 * 버튼 그룹 스타일
 */
export const buttonGroup = css`
  display: inline-flex;
  
  button {
    border-radius: 0;
    
    &:first-of-type {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
    
    &:last-of-type {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
    
    &:not(:last-of-type) {
      border-right: none;
    }
  }
`;

/**
 * 통합 버튼 스타일 가이드 객체
 */
export const aigleButtonStyles = {
  contained: containedButton,
  outlined: outlinedButton,
  transparent: transparentButton,
  size: buttonSize,
  icon: iconButtonStyle,
  iconOnly: iconOnlyButton,
  fullWidth: fullWidthButton,
  group: buttonGroup,
};

/**
 * TypeScript 타입 정의
 */
export type ContainedButtonStyles = typeof containedButton;
export type OutlinedButtonStyles = typeof outlinedButton;
export type ButtonSizeStyles = typeof buttonSize;
export type IconOnlyButtonStyles = typeof iconOnlyButton;
export type AigleButtonStyles = typeof aigleButtonStyles;

// Default export
export default aigleButtonStyles;

